/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
'use strict';

import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';
import { DashGisUtil } from '../../../../modules/shared/dash-gis-util';
import { AutoLoginConfig } from '../../../../types/Auth';
import { AppConfigState } from '../../../state/appConfig/app-config.state';
import { BeginPasswordLogin, InitOpenIdSignUp, LoginFailed, PrepareToLogin } from '../../state/auth.actions';
import { LoginPayload, PrepareToLoginPayload } from '../../state/auth.model';
import { AuthState } from '../../state/auth.state';
import { LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

interface LoginData {
    username: FormControl<string>;
    password: FormControl<string>;
    rememberMe: FormControl<boolean>;
}

@Component({
    selector: 'dash-gis-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [
        LayoutDirective,
        LayoutAlignDirective,
        MatCard,
        MatCardHeader,
        MatCardContent,
        CdkTrapFocus,
        FormsModule,
        ReactiveFormsModule,
        NgTemplateOutlet,
        MatCheckbox,
        MatProgressSpinner,
        MatFormField,
        MatLabel,
        MatInput,
        MatButton,
        AsyncPipe,
    ],
})
export class LoginComponent implements OnInit {
    autoLogin$: Observable<AutoLoginConfig> = inject(Store).select(AppConfigState.autoLogin);
    enableRemember$: Observable<boolean> = inject(Store).select(AppConfigState.enableRemember);
    authenticators$: Observable<string[]> = inject(Store).select(AuthState.loginMethods);
    openIdButtonText$: Observable<string> = inject(Store).select(AuthState.openIdButtonText);
    openIdSignupButtonText$: Observable<string> = inject(Store).select(AuthState.openIdSignupButtonText);
    loginInProgress$: Observable<boolean> = inject(Store).select(AuthState.authenticationInProgress);

    needsForgotPassword$: Observable<boolean>;

    loginForm: FormGroup<LoginData>;

    invalidLogin = false;

    constructor(
        private fb: FormBuilder,
        private store: Store,
        private actions$: Actions,
        private oauthService: OAuthService
    ) {
        this.loginForm = this.fb.group(
            {
                username: ['', Validators.required],
                password: ['', Validators.required],
                rememberMe: [false],
            },
            { updateOn: 'change' }
        );
    }

    ngOnInit() {
        this.actions$.pipe(ofActionSuccessful(LoginFailed)).subscribe(() => {
            this.invalidLogin = true;
        });

        this.needsForgotPassword$ = this.authenticators$.pipe(
            map((authList) => authList.findIndex((a) => a === 'password') !== -1)
        );
    }

    guestLogin() {
        this.invalidLogin = false;
        const loginPayload$ = this.autoLogin$.pipe(
            take(1),
            map(
                (autoLogin) =>
                    ({
                        credentials: {
                            username: autoLogin.guestuser,
                            password: autoLogin.guestpassword,
                        },
                        remember: false,
                    } as LoginPayload)
            )
        );
        this.store
            .dispatch(new PrepareToLogin({ method: 'password', remember: false }))
            .pipe(
                concatMap(() => loginPayload$),
                concatMap((payload) => this.store.dispatch(new BeginPasswordLogin(payload)))
            )
            .subscribe();
    }

    doLogin() {
        this.invalidLogin = false;
        const enableRemember = this.store.selectSnapshot(AppConfigState.enableRemember);
        const preparePayload: PrepareToLoginPayload = {
            method: 'password',
            remember: enableRemember && this.loginForm.get('rememberMe').value,
        };
        const payload: LoginPayload = {
            credentials: {
                username: this.loginForm.get('username').value,
                password: this.loginForm.get('password').value,
            },
        };
        this.store
            .dispatch(new PrepareToLogin(preparePayload))
            .pipe(concatMap(() => this.store.dispatch(new BeginPasswordLogin(payload))))
            .subscribe();
    }

    oauthLogin() {
        const requested = this.store.selectSnapshot(AuthState.requestedRoute);
        let url = null;
        if (requested?.url) {
            const dummy = 'file://dummy';
            const requestedUrl = new URL(requested.url, dummy);
            if (requested.queryParams) {
                Object.keys(requested.queryParams).forEach((q) =>
                    requestedUrl.searchParams.append(q, requested.queryParams[q])
                );
            }
            url = DashGisUtil.removeSubstring(dummy, requestedUrl.toString());
        }

        const enableRemember = this.store.selectSnapshot(AppConfigState.enableRemember);
        const preparePayload: PrepareToLoginPayload = {
            method: 'oidc',
            remember: enableRemember && this.loginForm.get('rememberMe').value,
        };

        this.store
            .dispatch(new PrepareToLogin(preparePayload))
            .pipe(take(1))
            .subscribe(() => this.oauthService.initLoginFlow(url));
    }

    oauthSignup() {
        this.store
            .dispatch(new InitOpenIdSignUp())
            .pipe(take(1))
            .subscribe(() => this.oauthService.initLoginFlow());
    }

    forgotPassword() {
        this.store.dispatch(new Navigate(['/auth/forgot']));
    }
}
